import React, { useMemo, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import AppButton from "../../common/AppButton";
import {
  useBillingPreview,
  usePostStudentBills
} from "../../../hooks/api/education/useBilling";

const BillingPreviewPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const [billingPeriodId, setBillingPeriodId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const previewInput = useMemo(
    () =>
      submitted && billingPeriodId
        ? { billingPeriodId, dueDate: dueDate || undefined }
        : null,
    [submitted, billingPeriodId, dueDate]
  );

  const { data: preview, isPending } = useBillingPreview(previewInput);
  const postMutation = usePostStudentBills();

  return (
    <Box data-testid="billing-preview-page" sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {t("billing-preview-title", "Bill students")}
      </Typography>

      <Box sx={{ display: "grid", gap: 2, maxWidth: 480, mb: 3 }}>
        <TextField
          label={t("billing-period-id", "Billing period ID")}
          value={billingPeriodId}
          onChange={(e) => setBillingPeriodId(e.target.value)}
          required
        />
        <TextField
          label={t("due-date", "Due date")}
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
        <AppButton
          disabled={!billingPeriodId}
          onClick={() => setSubmitted(true)}
        >
          {t("preview-bills", "Preview bills")}
        </AppButton>
      </Box>

      {submitted && isPending && (
        <CommonLoader
          loadingMessage={t("loading-billing-preview", "Calculating bills...")}
        />
      )}

      {preview && (
        <>
          <Typography sx={{ mb: 1 }}>
            {t(
              "billing-preview-summary",
              "{{count}} students · total {{total}}",
              {
                count: preview.studentCount ?? 0,
                total: preview.totalAmount ?? 0
              }
            )}
          </Typography>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", mb: 2 }}>
            {(preview.lines ?? []).map((line) => (
              <Box
                component="li"
                key={line.studentId}
                sx={{
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider"
                }}
              >
                <Typography fontWeight={600}>
                  {line.studentName} ({line.studentCode})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {line.totalAmount}
                </Typography>
              </Box>
            ))}
          </Box>
          <AppButton
            disabled={
              postMutation.isPending || (preview.lines?.length ?? 0) === 0
            }
            onClick={async () => {
              await postMutation.mutateAsync({
                billingPeriodId,
                dueDate: dueDate || undefined
              });
            }}
          >
            {t("post-bills", "Post bills")}
          </AppButton>
        </>
      )}
    </Box>
  );
};

export default BillingPreviewPage;
